import { request } from './client';

// Customer-facing Mini League endpoints. All errors arrive as
// ApiError with body {error: message} (401 when not authenticated;
// permission failures surface as 400 with a manager-only message).

// inviteePlayerIds get an invitation to accept or decline once the league exists.
export function createLeague({ name, gafferEnabled = false, inviteePlayerIds = [] }) {
  return request('/api/customer/mini-leagues', {
    method: 'POST',
    body: { name, gafferEnabled, inviteePlayerIds },
  });
}

// Manager only. Creates the first game for that tournament as a side effect.
export function addTournament(leagueId, { tournamentId, startingMatchWeek, gameName }) {
  return request(`/api/customer/mini-leagues/${leagueId}/tournaments`, {
    method: 'POST',
    body: { tournamentId, startingMatchWeek, gameName },
  });
}

// Joins the league and all of its current open games, OR raises a request if the
// league's first game has already started. Resolves either way — check
// result.outcome, which is 'JOINED' or 'REQUESTED'. Only real failures throw.
export function joinLeague(leagueCode) {
  return request('/api/customer/mini-leagues/join', { method: 'POST', body: { leagueCode } });
}

// Manager only. Pending requests, each carrying the asker's history.
export function getJoinRequests(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}/requests`);
}

export function acceptJoinRequest(leagueId, requestId) {
  return request(`/api/customer/mini-leagues/${leagueId}/requests/${requestId}/accept`, { method: 'POST' });
}

export function rejectJoinRequest(leagueId, requestId) {
  return request(`/api/customer/mini-leagues/${leagueId}/requests/${requestId}/reject`, { method: 'POST' });
}

export function acceptAllJoinRequests(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}/requests/accept-all`, { method: 'POST' });
}

// joinGameByCode is gone along with its endpoint. Joining a league's game
// without becoming a league member is no longer a state that exists - use
// games.joinGame, which now enrols you in the league behind the game.

export function getMyLeagues() {
  return request('/api/customer/mini-leagues/my-leagues');
}

export function getLeague(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}`);
}

// Roster of everyone in the league, including members pending removal.
// Requires a session (unlike the leaderboard, which is public).
export function getMembers(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}/members`);
}

export function getLeaderboard(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}/leaderboard`);
}

export function getNotices(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}/notices`);
}

export function addMember(leagueId, playerId) {
  return request(`/api/customer/mini-leagues/${leagueId}/members`, {
    method: 'POST',
    body: { playerId },
  });
}

export function removeMember(leagueId, playerId) {
  return request(`/api/customer/mini-leagues/${leagueId}/members/${playerId}`, {
    method: 'DELETE',
  });
}

// Any member can fork. Carries the chosen tournaments into a brand new league
// run by the caller; the source league is untouched. Everyone in
// inviteePlayerIds gets an invitation to accept or decline. Returns the new
// league, whose code is still worth sharing with anyone you could not name.
export function forkLeague(leagueId, { name, tournamentIds, inviteePlayerIds = [] }) {
  return request(`/api/customer/mini-leagues/${leagueId}/fork`, {
    method: 'POST',
    body: { name, tournamentIds, inviteePlayerIds },
  });
}

// Manager only, and only when no tournament has a game under way. Leaves the
// league vacant for any member to claim.
export function resignAsManager(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}/resign`, { method: 'POST' });
}

/**
 * Manager only. Calls the league off for good.
 *
 * Unlike resigning, this is allowed with games under way - it is the authority a manager
 * already has over each of the league's games one at a time, applied to all of them. Every
 * live game is cancelled with the league and the reason travels onto them, which is why it
 * is required rather than optional.
 */
export function cancelLeague(leagueId, reason) {
  return request(`/api/customer/mini-leagues/${leagueId}/cancel`, {
    method: 'POST',
    body: { reason },
  });
}

// Vacant leagues only. First member in wins.
export function claimLeague(leagueId) {
  return request(`/api/customer/mini-leagues/${leagueId}/claim`, { method: 'POST' });
}

export function setStake(leagueId, miniLeagueTournamentId, playerId, playingForMoney) {
  return request(
    `/api/customer/mini-leagues/${leagueId}/tournaments/${miniLeagueTournamentId}/stakes/${playerId}`,
    { method: 'PUT', body: { playingForMoney } }
  );
}

/**
 * Manager only. Sets whether games this league creates from here on include the Gaffer.
 * Not retroactive - a game already running keeps its own setting.
 */
export function setGafferEnabled(leagueId, enabled) {
  return request(`/api/customer/mini-leagues/${leagueId}/gaffer`, {
    method: 'PUT',
    body: { enabled },
  });
}

export function acknowledgeNotice(noticeId) {
  return request(`/api/customer/mini-leagues/notices/${noticeId}/acknowledge`, { method: 'PUT' });
}
